import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Button from '../components/shared/Button';
import Alert from '../components/shared/Alert';
import PasswordStrengthIndicator from '../components/auth/PasswordStrengthIndicator';
import logo from '../assets/logo-laranja.png';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validar token ao carregar a página
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Token de redefinição não fornecido');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(`/api/auth/reset-token/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setIsValidToken(true);
          setTokenEmail(data.email);
        } else {
          setError(data.error || 'Token inválido ou expirado');
          setIsValidToken(false);
        }
      } catch {
        setError('Erro ao validar o token. Tente novamente.');
        setIsValidToken(false);
      } finally {
        setIsVerifying(false);
      }
    };

    validateToken();
  }, [token]);

  const validateForm = (): boolean => {
    if (!newPassword || !confirmPassword) {
      setError('Todos os campos são obrigatórios');
      return false;
    }

    if (newPassword.length < 8) {
      setError('A palavra-passe deve ter pelo menos 8 caracteres');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('A palavra-passe e a confirmação não coincidem');
      return false;
    }

    // Validar requisitos da palavra-passe
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[@#!$%&*]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setError('A palavra-passe não cumpre todos os requisitos');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token, 
          newPassword, 
          confirmPassword 
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Ocorreu um erro ao redefinir a sua palavra-passe');
      }
    } catch {
      setError('Ocorreu um erro ao conectar com o servidor. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  // Estado de carregamento/verificação
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto h-48 w-48 flex items-center justify-center">
              <img
                src={logo}
                alt="doBeco Padaria"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
              <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
                Validando token...
              </h2>
              <p className="mt-2 text-center text-sm text-brand-text-muted">
                Por favor, aguarde enquanto validamos seu token de redefinição.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Token inválido
  if (!isVerifying && !isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-48 w-48 flex items-center justify-center">
              <img
                src={logo}
                alt="doBeco Padaria"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
              <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
                Token Inválido
              </h2>
              <p className="mt-2 text-center text-sm text-brand-text-muted">
                {error || 'O token de redefinição é inválido ou expirou.'}
              </p>
            </div>
          </div>

          <div className="mt-8">
            <Alert type="error" message={error || 'Token inválido ou expirado'} />
          </div>

          <div className="mt-6 space-y-3">
            <Link to="/forgot-password">
              <Button variant="primary" className="w-full">
                Solicitar Novo Token
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                Voltar para o Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Sucesso na redefinição
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-48 w-48 flex items-center justify-center">
              <img
                src={logo}
                alt="doBeco Padaria"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
              <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
                Palavra-Passe Redefinida!
              </h2>
              <p className="mt-2 text-center text-sm text-brand-text-muted">
                A sua palavra-passe foi redefinida com sucesso.
              </p>
            </div>
          </div>

          <div className="mt-8">
            <Alert type="success" message="A sua palavra-passe foi redefinida com sucesso. Você já pode fazer login com a nova palavra-passe." />
          </div>

          <div className="mt-6">
            <Link to="/login">
              <Button variant="primary" className="w-full">
                Fazer Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Formulário de redefinição
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-48 w-48 flex items-center justify-center">
            <img
              src={logo}
              alt="doBeco Padaria"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
            <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
              Redefinir Palavra-Passe
            </h2>
            <p className="mt-2 text-center text-sm text-brand-grey[600]">
              {tokenEmail && `Para o email: ${tokenEmail}`}
            </p>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Nova Senha */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-brand-text-primary">
              Nova Palavra-Passe
            </label>
            <div className="mt-1">
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-brand-neutral-300 placeholder:text-brand-text-muted text-brand-text-primary rounded-md focus:outline-none focus:ring-brand-primary-500 focus:border-brand-primary-500 sm:text-sm bg-white"
                placeholder="Digite a nova palavra-passe"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Indicador de Força da Senha */}
          {newPassword && (
            <div className="mt-2">
              <PasswordStrengthIndicator password={newPassword} />
            </div>
          )}

          {/* Confirmar Nova Senha */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-brand-text-primary">
              Confirmar Nova Palavra-Passe
            </label>
            <div className="mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-brand-neutral-300 placeholder:text-brand-text-muted text-brand-text-primary rounded-md focus:outline-none focus:ring-brand-primary-500 focus:border-brand-primary-500 sm:text-sm bg-white"
                placeholder="Confirme a nova palavra-passe"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Erro */}
          {error && (
            <Alert type="error" message={error} />
          )}

          {/* Botão de envio */}
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              loading={isLoading}
              className="w-full"
            >
              {isLoading ? 'Redefinindo...' : 'Redefinir Palavra-Passe'}
            </Button>
          </div>
        </form>

        {/* Links auxiliares */}
        <div className="mt-6">
          <div className="flex items-center justify-center">
            <div className="text-sm">
              <Link to="/login" className="font-medium text-brand-primary-500 hover:text-brand-primary-400">
                Voltar para o Login
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-brand-text-muted">
          doBeco Padaria - Sistema de Controlo de Manutenção
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;